import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Instagram, Youtube } from "lucide-react";
import ReviewsSection from "@/components/ReviewsSection";
import { BigHitsGrid, BigHitModal } from "@/pages/BigHits";

const HERO_IMG = "https://images.unsplash.com/photo-1614292253389-bd2c1f89cd0e";
const CARDS_IMG = "https://images.unsplash.com/photo-1599873265732-4511510f0908";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [settings, setSettings] = useState({});
  const [bigHits, setBigHits] = useState([]);
  const [activeHit, setActiveHit] = useState(null);

  useEffect(() => {
    api.get("/products/featured").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/big-hits?limit=4").then((r) => setBigHits(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-40 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,10,0.5) 0%, rgba(10,10,10,0.95) 100%)" }} />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36 grid md:grid-cols-[1fr_auto] gap-10 items-center">
          <div>
          <div className="text-label text-[var(--cl-primary)] mb-4">{settings.hero_kicker || "Lobster5 · Sports Card Marketplace"}</div>
          <h1 className="font-display text-5xl sm:text-6xl md:text-8xl leading-[0.9] max-w-4xl">
            {settings.hero_line1 || "Premium Singles."}<br />
            {settings.hero_line2 || "Mystery Pack Auctions."}<br />
            <span style={{ color: "var(--cl-primary)" }}>{settings.hero_line3 || "Live on Whatnot."}</span>
          </h1>
          <p className="mt-6 max-w-xl text-zinc-300 text-lg">
            {settings.hero_subtitle || "Card Lobster runs live auctions on Whatnot most nights, sells on eBay, and drops highlights on TikTok."}
          </p>
          <div className="flex flex-wrap gap-4 mt-8">
            <a href={settings.hero_cta_primary_url || "https://whatnot.com"} target="_blank" rel="noreferrer" className="btn-primary" data-testid="hero-whatnot-btn">
              {settings.hero_cta_primary_label || "Watch on Whatnot"}
            </a>
            <Link to="/shop" className="btn-outline" data-testid="hero-shop-btn">Shop Singles</Link>
            <Link to="/subscribe" className="btn-outline" data-testid="hero-join-btn">Join the Club</Link>
          </div>
          </div>
          <div className="hidden md:block relative">
            <div className="absolute -inset-6 rounded-full bg-[var(--cl-primary)]/20 blur-3xl" />
            <img
              src="/brand/card-lobster-mascot.jpg"
              alt="Card Lobster mascot"
              className="relative w-[380px] h-[380px] rounded-full object-cover ring-4 ring-[var(--cl-primary)]/40 shadow-[0_30px_80px_-20px_rgba(255,59,48,0.5)]"
            />
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-label text-zinc-500">Trending</div>
            <h2 className="font-display text-4xl sm:text-5xl mt-2">Featured Singles</h2>
          </div>
          <Link to="/shop" className="text-label text-[var(--cl-orange)] hover:underline" data-testid="view-all-shop">
            View All →
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {featured.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* BIG HITS TEASER */}
      {bigHits.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-20" data-testid="home-big-hits">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-label text-[var(--cl-primary)]">{settings.big_hits_kicker || "Big Hits"}</div>
              <h2 className="font-display text-4xl sm:text-5xl mt-2">{settings.big_hits_title || "Recent Bangers"}</h2>
            </div>
            <Link to="/big-hits" className="text-label text-[var(--cl-orange)] hover:underline" data-testid="view-all-big-hits">
              See All →
            </Link>
          </div>
          <BigHitsGrid hits={bigHits} onOpen={setActiveHit} />
        </section>
      )}
      <BigHitModal hit={activeHit} onClose={() => setActiveHit(null)} />

      {/* BENTO */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-20">
        <div className="grid md:grid-cols-3 gap-6">
          <Link to="/mystery-packs" className="md:col-span-2 relative rounded-md overflow-hidden border border-white/10 min-h-[320px] group" data-testid="bento-mystery">
            <img src="https://images.unsplash.com/photo-1577705998148-6da4f3963bc8" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105" style={{ transition: "transform 0.5s ease" }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 100%)" }} />
            <div className="relative p-10 h-full flex flex-col justify-end">
              <div className="text-label text-[var(--cl-gold)]">Wholesale</div>
              <h3 className="font-display text-4xl sm:text-5xl mt-2">Mystery Packs</h3>
              <p className="text-zinc-300 mt-2 max-w-md">Single packs to full 20-pack wholesale cases. Guaranteed hits, transparent odds.</p>
            </div>
          </Link>
          <Link to="/subscribe" className="relative rounded-md overflow-hidden border border-[var(--cl-primary)]/40 min-h-[320px] group bg-gradient-to-br from-[#1a0505] to-[var(--cl-surface)]" data-testid="bento-club">
            <div className="relative p-10 h-full flex flex-col justify-between">
              <div>
                <div className="text-label text-[var(--cl-primary)]">Members Club</div>
                <h3 className="font-display text-4xl mt-2">3 Tiers.<br/>All Access.</h3>
                <p className="text-zinc-400 mt-3 text-sm">Monthly packs, storewide discounts, priority breaks, VIP Discord.</p>
              </div>
              <div className="flex items-center gap-3 mt-6">
                <span className="btn-primary text-sm">Compare Tiers</span>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* SOCIAL BAR */}
      <ReviewsSection />

      <section className="border-t border-white/5 bg-[var(--cl-surface)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14">
          <div className="text-label text-zinc-500 text-center">Join The Community</div>
          <h3 className="font-display text-3xl sm:text-4xl text-center mt-2">Watch. Bid. Follow.</h3>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <SocialTile href="https://whatnot.com" label="Whatnot" testid="social-tile-whatnot" primary><span className="text-xs font-black">WN</span></SocialTile>
            <SocialTile href="https://tiktok.com" label="TikTok" testid="social-tile-tiktok"><span className="text-xs font-black">TT</span></SocialTile>
            <SocialTile href="https://ebay.com" label="eBay" testid="social-tile-ebay"><span className="text-xs font-black">eB</span></SocialTile>
            <SocialTile href="https://instagram.com" label="Instagram" testid="social-tile-ig"><Instagram size={18}/></SocialTile>
            <SocialTile href="https://youtube.com" label="YouTube" testid="social-tile-yt"><Youtube size={18}/></SocialTile>
          </div>
        </div>
      </section>
    </div>
  );
}

function SocialTile({ href, label, children, testid, primary }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
       className={`flex items-center gap-3 px-5 py-3 rounded-md border transition-colors ${primary ? "border-[var(--cl-primary)] bg-[var(--cl-primary)]/10 text-white hover:bg-[var(--cl-primary)]/20" : "border-white/10 hover:border-[var(--cl-orange)] hover:text-[var(--cl-orange)]"}`}>
      {children}<span className="text-label">{label}</span>
    </a>
  );
}
